import axios, { AxiosError, AxiosResponse } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;

// Response interceptor for handling errors
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Don't try to refresh for auth endpoints or if already refreshing
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/');

    if (error.response?.status === 401 && !isAuthEndpoint && !isRefreshing) {
      isRefreshing = true;
      try {
        await api.post('/auth/refresh');
        isRefreshing = false;
        // Retry original request
        return api.request(originalRequest!);
      } catch (refreshError) {
        isRefreshing = false;
        // Only redirect if not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  refresh: () => api.post('/auth/refresh'),
  me: () => api.get('/auth/me'),
};

// Users API
export const usersApi = {
  getProfile: () => api.get('/users/me'),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.put('/users/me/password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
  getPreferences: () => api.get('/users/me/preferences'),
  updatePreferences: (prefs: {
    default_hours?: number;
    map_center_lat?: string;
    map_center_lng?: string;
    map_zoom?: number;
  }) => api.put('/users/me/preferences', prefs),
};

// Potholes API
export const potholesApi = {
  // Get currently online potholes as points (for Points view)
  getAllPotholes: (hours?: number) =>
    api.post('/potholes/all', {}, { params: hours ? { hours } : {} }),

  // Get potholes for heatmap/clusters (includes historical data)
  getHeatmapPotholes: (hours: number = 24) =>
    api.post('/potholes/heatmap', {}, { params: { hours } }),
};

// Admin API
export const adminApi = {
  // Users
  getUsers: (skip: number = 0, limit: number = 50) =>
    api.get('/admin/users', { params: { skip, limit } }),
  getUser: (userId: number) => api.get(`/admin/users/${userId}`),
  createUser: (data: {
    email: string;
    password: string;
    role?: string;
    is_active?: boolean;
  }) => api.post('/admin/users', data),
  updateUser: (
    userId: number,
    data: {
      email?: string;
      role?: string;
      is_active?: boolean;
      password?: string;
    }
  ) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId: number) => api.delete(`/admin/users/${userId}`),
  revokeAccess: (userId: number) => api.post(`/admin/users/${userId}/revoke`),

  // Logs
  getLogs: (params: {
    page?: number;
    page_size?: number;
    user_id?: number;
    action?: string;
    start_date?: string;
    end_date?: string;
  }) => api.get('/admin/logs', { params }),

  // Blocked IPs
  getBlockedIPs: () => api.get('/admin/blocked-ips'),
  unblockIP: (ip: string) =>
    api.post(`/admin/blocked-ips/${encodeURIComponent(ip)}/unblock`),

  // Stats
  getStats: () => api.get('/admin/stats'),
};

export default api;
